import { Fragment } from "react";
import { parseGapSentence } from "../../../../helpers/OnlineTestHelper";
import { getAnswerStateStyles } from "../../../../constants/StudentResultAnswerColors";
import { getTeacherCommentTone } from "../../../../constants/StudentResultCommentTones";
import {
    StudentResultAnswerStates,
    type StudentResultAnswer,
    type StudentResultExercise,
} from "../../../../helpers/StudentResultModel";
import StudentResultAnswerCheck from "../StudentResultAnswerCheck";
import StudentResultAnswerRow from "../StudentResultAnswerRow";
import StudentResultAnswerScore from "../StudentResultAnswerScore";
import StudentResultAnswerStateLabel from "../StudentResultAnswerStateLabel";
import StudentResultNoAnswerPlaceholder from "../StudentResultNoAnswerPlaceholder";

interface StudentResultAnswersFillGapProps {
    exercise: StudentResultExercise;
}

/** Gap sentences: the student's word sits inside the sentence, coloured by its state. */
const StudentResultAnswersFillGap = (props: StudentResultAnswersFillGapProps) => {
    const renderGap = (answer: StudentResultAnswer) => {
        const styles = getAnswerStateStyles(answer.state);
        const isUnanswered = answer.state === StudentResultAnswerStates.NO_ANSWER;

        return (
            <span className={`inline-flex min-w-0 px-xs py-xxs rounded-xsmall-container ${styles.background}`}>
                <span
                    className={`font-semibold text-l border-b-2 border-dashed border-p6-grey-light wrap-anywhere ${styles.text}`}
                >
                    {isUnanswered ? <StudentResultNoAnswerPlaceholder /> : answer.studentAnswer}
                </span>
            </span>
        );
    };

    /**
     * A sentence without a gap marker still shows the answer, after the text — the answer must never
     * disappear because of how the sentence was written. The check follows the last word rather than
     * sitting at the row's edge. The verdict label is emitted once per answer, not per gap: a sentence
     * with several markers repeats the same answer in each.
     */
    const renderSentence = (answer: StudentResultAnswer) => {
        const parts = parseGapSentence(answer.gapSentence);
        const hasGap = parts.length > 1;

        return (
            // The floor is what pushes the score onto its own line: without it the sentence's min-content
            // (tiny, the gap breaks anywhere) would let the score squeeze it into a sliver instead.
            <p className="flex-1 min-w-[70%] flex flex-wrap items-center gap-x-s gap-y-xs text-l text-p6-grey-dark">
                <StudentResultAnswerStateLabel state={answer.state} />
                {parts.map((part, partIndex) => (
                    <Fragment key={partIndex}>
                        {part && <span>{part}</span>}
                        {partIndex < parts.length - 1 && renderGap(answer)}
                    </Fragment>
                ))}
                {!hasGap && renderGap(answer)}
                <StudentResultAnswerCheck state={answer.state} />
            </p>
        );
    };

    /**
     * The score sits at the end of the sentence's line and moves to its own right-aligned line *above*
     * once it would leave the sentence less than its floor — `flex-wrap-reverse` stacks the wrapped
     * line on top rather than below.
     */
    const renderBody = (answer: StudentResultAnswer) => (
        <div className="flex flex-wrap-reverse items-center justify-end gap-s">
            {renderSentence(answer)}
            <StudentResultAnswerScore
                answer={answer}
                isScored={props.exercise.isScored}
            />
        </div>
    );

    return (
        <ul
            className="flex flex-col"
            data-testid="student-result-answers-fill-gap"
        >
            {props.exercise.answers.map((answer, index) => (
                <StudentResultAnswerRow
                    key={answer.id}
                    isLast={index === props.exercise.answers.length - 1}
                    comment={answer.teacherComment}
                    tone={getTeacherCommentTone(answer.state)}
                >
                    {renderBody(answer)}
                </StudentResultAnswerRow>
            ))}
        </ul>
    );
};

export default StudentResultAnswersFillGap;
